import Vue from 'vue';
import Vuex from 'vuex';
import api from '../api';
import { formatDateMixin } from '@/mixins/formatDateMixin';

import chat from '@/store/modules/chat';
import calendar from '@/store/modules/calendar';
import company from '@/store/modules/company';

Vue.use(Vuex);
Vuex.Store.prototype.$api = api;

function playSound (soundObj) { // KEK
  if (soundObj === 'receiveMessageSound') {
    const audio = new Audio('/static/assets/music/beyond-doubt.mp3'); // path to file
    audio.volume = 0.2;
    audio.play();
  }
  if (soundObj === 'sendMessageSound') {
    const audio = new Audio('/static/assets/music/just-maybe.mp3'); // path to file
    audio.volume = 0.2;
    audio.play();
  }
}

const store = new Vuex.Store({
  modules: {
    chat,
    calendar,
    company,
  },
  state: {
    dragOptions: {
      animation: 300,
      group: 'description',
      disabled: false,
      ghostClass: 'ghost',
    },
    window: {
      width: 0,
      height: 0,
    },

    messages: [],
    suggestions: [],
    placeholder: { text: 'Message', type: 'text' },

    account: {},
    company: {},
    invites: [],
    integrations: {},
    workflows: [],

    task_template: {},
    tasks: [],
    projects: [],
    members: [],

    //

    epic_links: [],
    sections: [],
    duration_actual_logs: [],

    //

    selected: {
      date: null,
      account_id: null,
      project_id: null,
      task: null,
      duration: 60,
      duration_actual: 0,
    },

    show_modals: {
      edit_task: false,
      create_task: false,
      edit_project: false,
      create_project: false,
      day_info: false,
      create_section: false,
      mobile_device: false,
      onboarding: false,
    },
    highlight_task_id: null,

    tasks_filter: {
      by_text: '',
      by_assignees: [],
      by_creators: [],
      by_user: 'all_people',
      by_priority: 'all',
      by_status: 'incomplete', // all, completed, incomplete
      by_plan_date: 'all', // all, planned, unplanned
      by_deadline_date: 'all', // all, with_deadline, without deadline
    },

    calendar_filter: {
      by_text: '',
      by_status: 'incomplete', // all, completed, incomplete
      by_plan_date: 'unplanned', // all, planned, unplanned
      by_user: 'all_people',
      by_priority: 'all',
      by_deadline_date: 'all', // all, with_deadline, without deadline
    },
    tasks_grouping: {
      first_level: null,
      second_level: null,
    },

    show_inbox: false,
  },
  getters: {
    show_inbox: function (state) {
      return state.show_inbox;
    },
    window: function (state) {
      return state.window;
    },
    messages: function (state) {
      return state.messages;
    },
    suggestions: function (state) {
      return state.suggestions;
    },
    placeholder: function (state) {
      return state.placeholder;
    },
    dragOptions: function (state) {
      return state.dragOptions;
    },
    highlight_task_id: function (state) {
      return state.highlight_task_id;
    },

    account: function (state) {
      return state.account;
    },
    company: function (state) {
      return state.company;
    },
    invites: function (state) {
      return state.invites;
    },
    integrations: function (state) {
      return state.integrations;
    },
    workflows: function (state) {
      return state.workflows;
    },
    task_template: function (state) {
      return state.task_template;
    },

    tasks: function (state) {
      return state.tasks;
    },

    tasks_filter: function (state) {
      return state.tasks_filter;
    },

    calendar_filter: function (state) {
      return state.calendar_filter;
    },

    tasks_grouping_first_level: function (state) {
      return state.tasks_grouping.first_level;
    },
    tasks_grouping_second_level: function (state) {
      return state.tasks_grouping.second_level;
    },

    tasks_with_filters: function (state) {
      let scopedTasks = state.tasks
        .filter(task => {
          return state.selected.project_id
            ? task.project_id === state.selected.project_id
            : true;
        });
      if (state.tasks_filter.by_text && state.tasks_filter.by_text.length !== 0) {
        scopedTasks = scopedTasks.filter(task => task.name.toLowerCase().includes(state.tasks_filter.by_text.toLowerCase()));
      }
      if (state.tasks_filter.by_status === 'incomplete') {
        scopedTasks = scopedTasks.filter(task => task.status === 'new' || task.status === 'wip');
      }
      if (state.tasks_filter.by_status === 'completed') {
        scopedTasks = scopedTasks.filter(task => task.status === 'done');
      }
      if (state.tasks_filter.by_plan_date === 'unplanned') {
        scopedTasks = scopedTasks.filter(task => !task.plan_date);
      }
      if (state.tasks_filter.by_plan_date === 'planned') {
        scopedTasks = scopedTasks.filter(task => task.plan_date);
      }
      if (state.tasks_filter.by_deadline_date === 'without_deadline') {
        scopedTasks = scopedTasks.filter(task => !task.deadline_date);
      }
      if (state.tasks_filter.by_deadline_date === 'with_deadline') {
        scopedTasks = scopedTasks.filter(task => task.deadline_date);
      }
      if (state.tasks_filter.by_user && state.tasks_filter.by_user !== 'all_people') {
        scopedTasks = scopedTasks.filter(task => task.assignee === state.tasks_filter.by_user);
      }
      if (state.tasks_filter.by_priority !== 'all') {
        scopedTasks = scopedTasks.filter(task => task.priority === state.tasks_filter.by_priority);
      }
      if (state.tasks_filter.by_assignees.length > 0) {
        scopedTasks = scopedTasks.filter(task => state.tasks_filter.by_assignees.indexOf(task.assignee) > -1);
      }
      scopedTasks = scopedTasks.sort((a, b) => (a.order - b.order ? a.order - b.order : a.created_at - b.created_at));
      return scopedTasks;
    },
    tasks_with_calendar_filter: function (state) {
      let scopedTasks = state.tasks
        .filter(task => !task.plan_date && task.status !== 'deleted' && (task.project_id === state.selected.project_id || !state.selected.project_id));

      if (state.tasks_filter.by_status === 'incomplete') {
        scopedTasks = scopedTasks.filter(task => task.status === 'new' || task.status === 'wip');
      }
      if (state.tasks_filter.by_status === 'completed') {
        scopedTasks = scopedTasks.filter(task => task.status === 'done');
      }
      if (state.tasks_filter.by_plan_date === 'unplanned') {
        scopedTasks = scopedTasks.filter(task => !task.plan_date);
      }
      if (state.tasks_filter.by_plan_date === 'planned') {
        scopedTasks = scopedTasks.filter(task => task.plan_date);
      }
      if (state.tasks_filter.by_deadline_date === 'without_deadline') {
        scopedTasks = scopedTasks.filter(task => !task.deadline_date);
      }
      if (state.tasks_filter.by_deadline_date === 'with_deadline') {
        scopedTasks = scopedTasks.filter(task => task.deadline_date);
      }
      if (state.tasks_filter.by_priority !== 'all') {
        scopedTasks = scopedTasks.filter(task => task.priority === state.tasks_filter.by_priority);
      }
      if (state.tasks_filter.by_assignees.length > 0) {
        scopedTasks = scopedTasks.filter(task => state.tasks_filter.by_assignees.indexOf(task.assignee) > -1);
      }
      if (state.tasks_filter.by_creators.length > 0) {
        scopedTasks = scopedTasks.filter(task => state.tasks_filter.by_creators.indexOf(task.created_by) > -1);
      }

      scopedTasks = scopedTasks.sort((a, b) => (a.order - b.order ? a.order - b.order : a.created_at - b.created_at));
      return scopedTasks;
    },

    get_task_by_id: (state) => (taskId) => {
      return state.tasks.find(task => task._id === taskId, 1);
    },

    members: function (state) {
      return state.members;
    },

    projects: function (state) {
      return state.projects;
    },

    epic_links: function (state) {
      return state.epic_links;
    },

    sections: function (state) {
      return state.sections;
    },

    duration_actual_logs: function (state) {
      return state.duration_actual_logs;
    },

    // SELECTED SECTION

    selected_date: function (state) {
      return state.selected.date;
    },
    selected_account_id: function (state) {
      return state.selected.account_id;
    },
    selected_project_id: function (state) {
      return state.selected.project_id;
    },
    selected_duration: function (state) {
      return state.selected.duration;
    },
    selected_duration_actual: function (state) {
      return state.selected.duration_actual;
    },
    selected_task: function (state) {
      return state.selected.task;
    },

    // MODALS

    show_modal_edit_task: state => {
      return state.show_modals.edit_task;
    },
    show_modal_create_task: state => {
      return state.show_modals.create_task;
    },
    show_modal_edit_project: state => {
      return state.show_modals.edit_project;
    },
    show_modal_create_project: state => {
      return state.show_modals.create_project;
    },
    show_modal_day_info: state => {
      return state.show_modals.day_info;
    },
    show_modal_create_section: state => {
      return state.show_modals.create_section;
    },
    show_modal_mobile_device: state => {
      return state.show_modals.mobile_device;
    },
    show_modal_onboarding: state => {
      return state.show_modals.onboarding;
    },

  },

  mutations: {
    setInboxView: function (state, inboxStatus = !state.show_inbox) {
      state.show_inbox = inboxStatus;
    },
    fetchAccount: function (state, account) {
      state.account = account;
    },
    fetchTaskTemplate: function (state, taskTemplate) {
      Vue.set(state, 'task_template', taskTemplate);
    },
    fetchTasks: function (state, tasks) {
      state.tasks = tasks;
    },
    fetchProjects: function (state, projects) {
      state.projects = projects;
    },
    fetchMembers: function (state, members) {
      state.members = members;
    },
    fetchCompany: function (state, company) {
      state.company = company;
    },
    fetchInvites: function (state, invites) {
      state.invites = invites;
    },
    fetchIntegrations: function (state, integrations) {
      state.integrations = integrations;
    },
    fetchEpicLinks: function (state, epicLinks) {
      state.epic_links = epicLinks;
    },
    fetchSections: function (state, sections) {
      state.sections = sections;
    },
    fetchDurationActualLogs: function (state, durationActualLogs) {
      state.duration_actual_logs = durationActualLogs;
    },
    fetchWorkflows: function (state, workflows) {
      state.workflows = workflows;
    },
    updateTask: function (state, newTask) {
      const existingIndex = state.tasks.findIndex(task => {
        return task._id === newTask._id;
      });
      if (existingIndex >= 0) {
        state.tasks.splice(existingIndex, 1);
      }
      state.tasks.push(newTask);
    },
    updateProject: function (state, newProject) {
      const existingIndex = state.projects.findIndex(project => project._id === newProject._id, 1);
      if (existingIndex >= 0) {
        state.projects.splice(existingIndex, 1);
      }
      state.projects.push(newProject);
    },
    reorderTasks: function (state) {
      state.tasks = state.tasks.sort((a, b) => a.order - b.order);
    },
    setHighlightTaskId: function (state, taskId) {
      Vue.set(state, 'highlight_task_id', taskId);
      // eslint-disable-next-line no-return-assign
      setTimeout(() => state.highlight_task_id = null, 3000);
    },
    setMessages: function (state, messages) {
      Vue.set(state, 'messages', messages);
    },
    setSuggestions: function (state, suggestions) {
      Vue.set(state, 'suggestions', suggestions);
    },
    setPlaceholder: function (state, placeholder) {
      Vue.set(state, 'placeholder', placeholder);
    },
    pushMessage: function (state, message) {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        if (state.messages[i]._id === undefined && !state.messages[i].is_emika) {
          state.messages.splice(i, 1);
          break;
        }
      }
      state.messages.push(message);
    },

    // SELECTED SECTION

    setSelectedTask: function (state, taskId) {
      const existingTask = state.tasks.find(task => task._id === taskId, 1);
      Vue.set(state.selected, 'task', existingTask || null);
    },

    setTaskFilter: function (state, payload) {
      Vue.set(state.tasks_filter, payload.slug, payload.value);
    },
    setCalendarFilter: function (state, payload) {
      Vue.set(state.calendar_filter, payload.slug, payload.value);
    },
    setTaskGroupingFirstLevel: function (state, payload) {
      Vue.set(state.tasks_grouping, 'first_level', payload);
    },
    setTaskGroupingSecondLevel: function (state, payload) {
      Vue.set(state.tasks_grouping, 'second_level', payload);
    },

    setSelectedDate: function (state, date) {
      Vue.set(state.selected, 'date', formatDateMixin.methods.formatDate(date));
    },
    setSelectedAccountId: function (state, accountId) {
      Vue.set(state.selected, 'account_id', accountId);
    },
    setSelectedProjectId: function (state, projectId) {
      Vue.set(state.selected, 'project_id', projectId);
    },
    setSelectedDuration: function (state, newDuration) {
      Vue.set(state.selected, 'duration', newDuration);
    },

    showModal: function (state, modalName) {
      Vue.set(state.show_modals, modalName, true);
    },
    hideModal: function (state, modalName) {
      Vue.set(state.show_modals, modalName, false);
    },

  },
  actions: {

    // ACCOUNT CALLS
    fetchAccount: function ({ commit }) {
      const self = this;
      return self.$api().get('/account/info', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            if (response.data.payload.status === 'inactive' && window.location.pathname !== '/auth') {
              window.location.pathname = '/auth';
            }
            const accountId = localStorage.getItem('selectedAccountId') ? localStorage.getItem('selectedAccountId') : response.data.payload._id;

            commit('fetchAccount', response.data.payload);
            commit('setSelectedAccountId', accountId);
            commit('setTaskFilter', { slug: 'by_assignees', value: [accountId] });

            // eslint-disable-next-line no-undef
            if (_.isEmpty(response.data.payload) || response.data.payload.status === 'inactive' || !response.data.payload.company_id) {
              if (window.location.pathname !== '/auth') {
                window.location.href = '/auth';
              }
            }
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateAccount: function ({ commit, state }, payload) {
      const account = payload || state.account;
      const self = this;
      self.$api().post('/account/update', {
        account: JSON.stringify(account),
      }, {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },

    // TASKS CALLS

    fetchTaskTemplate: function ({ commit }) {
      const self = this;
      self.$api().get('/tasks/template', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('fetchTaskTemplate', response.data.payload.default);
            if (response.data.payload.options.projects.length > 0 && !localStorage.getItem('selectedProjectId')) {
              commit('setSelectedProjectId', response.data.payload.options.projects[0]._id);
            }
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    fetchTasks: function ({ commit }, payload = {}) {
      this.$api().get('/tasks/fetch',
        {
          params: Object.assign({ token: localStorage.getItem('token') }, payload),
        })
        .then(response => {
          if (response.data.ok) {
            commit('fetchTasks', response.data.payload);
          } else {
            this.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    fetchProjects: function ({ commit }) {
      const self = this;
      return self.$api().get('/projects/fetch', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('fetchProjects', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    fetchMembers: function ({ commit }) {
      const self = this;
      self.$api().get('/members/fetch', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('fetchMembers', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    fetchInvites: function ({ commit }) {
      const self = this;
      self.$api().get('/company/invitations/fetch', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('fetchInvites', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    fetchIntegrations: function ({ commit }) {
      const self = this;
      return self.$api().get('/company/integrations/fetch', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('fetchIntegrations', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    fetchCompany: function ({ commit, state }) {
      const self = this;
      return self.$api().get('/company/fetch', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok && response.data.payload.transactions) {
            let isChanged = false;
            for (const transaction of response.data.payload.transactions) {
              if (!transaction.is_fb_event_sent && transaction.type === 'deposit') {
                window.fbq('track', 'Purchase', {
                  value: transaction.amount,
                  currency: 'USD',
                });
                try {
                  window.ga('ec:setAction', 'purchase', {
                    id: transaction._id,
                    affiliation: 'Google Store - Online',
                    revenue: transaction.amount,
                  });
                } catch (e) {
                  console.log('GA error: ', e);
                }
                isChanged = true;
              }
            }
            if (isChanged) {
              self.$api().post('/company/mark_events_sent', {}, {
                params: {
                  token: localStorage.getItem('token'),
                },
              });
            }
            commit('fetchCompany', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateCompany: function ({ commit, state }) {
      const self = this;
      self.$api().post('/company/update', { company: JSON.stringify(state.company) }, {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('fetchCompany', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    fetchMessages: function ({ commit, state }, offset = 0) { // KEK
      const self = this;
      self.$api().get('/chat/fetch_messages', {
        params: {
          token: localStorage.getItem('token'),
          offset,
          limit: 25,
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            const messages = response.data.payload.messages;
            const newMess = messages.reverse(); // .concat(state.messages);
            commit('setMessages', newMess);
            commit('setSuggestions', response.data.payload.suggestions);
            commit('setPlaceholder', response.data.payload.placeholder);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
          state.loadingMessages = false;
        });
    },
    sendMessage: function ({ commit }, userMessage) { // KEK
      const self = this;
      playSound('sendMessageSound');
      commit('setSuggestions', []);
      self.$api().post('/chat/send_message', {}, {
        params: {
          token: localStorage.getItem('token'),
          text: userMessage.text,
          is_password: userMessage.is_password,
        },
      });
    },

    getMessage: function ({ dispatch, commit }, message) { // KEK
      setTimeout(function () {
        if (message.is_emika && localStorage.getItem('lastSoundedMessage') !== message._id) {
          localStorage.setItem('lastSoundedMessage', message._id);
          // playSound('receiveMessageSound');
        }
        commit('pushMessage', message);
      }, message.delay);
      // self.ai_thinking = false;
    },

    createProject: function ({ dispatch, commit }, payload) {
      const self = this;

      self.$api().post('/projects/create', { project: JSON.stringify(payload) }, {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('setSelectedProjectId', response.data.payload._id);
            dispatch('fetchProjects');
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateProject: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/projects/' + payload._id + '/update', { project: JSON.stringify(payload) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            // commit('updateProject', response.data.payload)
            dispatch('fetchProjects');
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },

    createTask: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/tasks/create', { task: JSON.stringify(payload) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('updateTask', response.data.payload);
            commit('setHighlightTaskId', response.data.payload._id);
            commit('setSelectedTask', response.data.payload._id);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateTask: function ({ commit }, payload) {
      if (!payload.deadline_date) { payload.deadline_date = null; }
      if (!payload.plan_date) { payload.plan_date = null; }
      const self = this;
      self.$api().post('/tasks/' + payload._id + '/update', { task: JSON.stringify(payload) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('updateTask', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateSelectedTask: function ({ commit, getters }) {
      const self = this;
      self.$api().post('/tasks/' + getters.selected_task._id + '/update', { task: JSON.stringify(getters.selected_task) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('updateTask', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateTasksOrder: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/tasks/update_order', { tasks: JSON.stringify(payload) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            // dispatch('fetchTasks')
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },

    // EPIC LINKS API

    fetchEpicLinks: function ({ commit }) {
      const self = this;
      self.$api().get('/projects/epic_links/fetch', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('fetchEpicLinks', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateEpicLinksOrder: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/projects/epic_links/update_order', { epic_links: JSON.stringify(payload) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {

          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    createEpicLink: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/projects/epic_links/create', { epic_link: JSON.stringify(payload) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            dispatch('fetchEpicLinks', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateEpicLink: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/projects/epic_links/' + payload._id + '/update', { epic_link: JSON.stringify(payload) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            dispatch('fetchEpicLinks', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    deleteEpicLink: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/projects/epic_links/' + payload + '/delete', {}, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            dispatch('fetchEpicLinks', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },

    // SECTIONS API

    fetchSections: function ({ commit }) {
      const self = this;
      self.$api().get('/projects/sections/fetch', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('fetchSections', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateSectionsOrder: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/projects/sections/update_order', { sections: JSON.stringify(payload) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {

          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    createSection: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/projects/sections/create', { section: JSON.stringify(payload) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            dispatch('fetchSections', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateSection: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/projects/sections/' + payload._id + '/update', { section: JSON.stringify(payload) }, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            dispatch('fetchSections', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    deleteSection: function ({ commit, dispatch }, payload) {
      const self = this;
      self.$api().post('/projects/sections/' + payload + '/delete', {}, {
        params: { token: localStorage.getItem('token') },
      })
        .then(function (response) {
          if (response.data.ok) {
            dispatch('fetchSections', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },

    // DURATION ACTUAL LOG API

    fetchDurationActualLogs: function ({ commit }) {
      const self = this;
      self.$api().get('/projects/duration_actual_logs/fetch', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('fetchDurationActualLogs', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },

    // WORKFLOWS API

    fetchWorkflows: function ({ commit }) {
      const self = this;
      return self.$api().get('/workflows/fetch', {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            commit('fetchWorkflows', response.data.payload);
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },
    updateWorkflow: function ({ commit }, payload) {
      const self = this;
      self.$api().post('/workflows/' + payload._id + '/update', { company: JSON.stringify(payload) }, {
        params: {
          token: localStorage.getItem('token'),
        },
      })
        .then(function (response) {
          if (response.data.ok) {
            // commit('fetchCompany', response.data.payload)
          } else {
            self.$app.$noty.error('An Error occurred: ' + response.data.error);
          }
        });
    },

  },
});

export default store;
